import React, {
	useEffect,
	useRef,
	forwardRef,
	useImperativeHandle,
	useState,
} from "react";
import axios from "axios";

function replaceHost(origin) {
	const url = new URL(origin);
	if (url.host.includes("akamaized")) {
		url.searchParams.set("no_replace", "true");
		url.searchParams.set("host", url.host);
		url.host = "stream-proxy.vtb.cat";
		url.searchParams.set("no_referer", "true");
	} else {
		url.host = "stream-proxy-sb.vtb.cat";
	}

	url.protocol = "https:";
	return url.toString();
}

const BVPlayer = forwardRef((props, ref) => {
	const [videoSrc, setVideoSrc] = useState("");
	const [audioSrc, setAudioSrc] = useState("");
	const [isPortrait, setIsPortrait] = useState(false); // 记录是否为竖屏视频

	const videoRef = useRef(null);
	const audioRef = useRef(null);
	const hasAppliedInitialRef = useRef(false);

	// 缓存外部传入的进度回调函数，避免频繁触发 useEffect 重绑
	const onTimeUpdateRef = useRef(props.onTimeUpdate);
	useEffect(() => {
		onTimeUpdateRef.current = props.onTimeUpdate;
	}, [props.onTimeUpdate]);

	useImperativeHandle(ref, () => ({
		seek: (time) => seekBoth(time),
		play: () => {
			videoRef.current?.play();
			audioRef.current?.play();
		},
		pause: () => {
			videoRef.current?.pause();
			audioRef.current?.pause();
		},
		getCurrentTime: () => {
			const active = document.hidden
				? audioRef.current
				: videoRef.current;
			return active?.currentTime || 0;
		},
		getDuration: () =>
			videoRef.current?.duration || audioRef.current?.duration || 0,
		video: videoRef.current,
		audio: audioRef.current,
	}));

	const seekBoth = (time) => {
		if (typeof time !== "number" || isNaN(time)) return;
		if (videoRef.current) videoRef.current.currentTime = time;
		if (audioRef.current) audioRef.current.currentTime = time;
	};

	// 取流
	useEffect(() => {
		if (!props.bv) return;
		hasAppliedInitialRef.current = false;
		setIsPortrait(false); // 重置竖屏状态

		axios
			.get(`/api/bilibili/view?bv=${props.bv}&cid=${props.cid}`)
			.then((res) => {
				var max = 0;
				res.data.video.forEach((video) => {
					if (video.id > max) {
						max = video.id;
					}
				});
				var v = res.data.video.filter((item) => item.id === max);
				setVideoSrc(replaceHost(v[v.length - 1].base_url));

				setAudioSrc(replaceHost(res.data.audio[0].base_url));
			});
	}, [props.bv, props.cid]);

	// 响应外部 props.currentTime 变更
	useEffect(() => {
		if (typeof props.currentTime === "number" && videoRef.current) {
			if (
				Math.abs(videoRef.current.currentTime - props.currentTime) > 0.5
			) {
				seekBoth(props.currentTime);
			}
		}
	}, [props.currentTime]);

	// 音视频联动、后台防挂起及实时进度广播
	useEffect(() => {
		const video = videoRef.current;
		const audio = audioRef.current;
		if (!video || !audio) return;

		// 1. 元数据加载：判断视频比例并注入初始播放时间
		const handleLoadedMetadata = () => {
			// 检测是否为竖屏视频 (高 > 宽)
			if (video.videoWidth && video.videoHeight) {
				setIsPortrait(video.videoHeight > video.videoWidth);
			}

			if (!hasAppliedInitialRef.current) {
				const target = props.initialTime ?? props.currentTime;
				if (typeof target === "number" && target > 0) {
					seekBoth(target);
				}
				hasAppliedInitialRef.current = true;
			}
		};

		const handlePlay = () => {
			audio.play().catch(() => {});
		};

		// 2. 关键防护：如果标签页在后台，忽略视频的 pause，让音频继续播
		const handlePause = () => {
			if (document.hidden) {
				return;
			}
			audio.pause();
		};

		// 音量与静音跟随
		const handleVolumeChange = () => {
			audio.volume = video.volume;
			audio.muted = video.muted;
		};

		// 拖动进度条同步
		const handleSeeked = () => {
			if (Math.abs(audio.currentTime - video.currentTime) > 0.2) {
				audio.currentTime = video.currentTime;
			}
		};

		// 3. 切回前台时画面对齐音频时间
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				if (Math.abs(video.currentTime - audio.currentTime) > 0.2) {
					video.currentTime = audio.currentTime;
				}
				if (!audio.paused && video.paused) {
					video.play().catch(() => {});
				}
			}
		};

		// 4. 实时进度回调：前台以视频为准，后台以音频为准
		const handleVideoTimeUpdate = () => {
			if (!document.hidden && onTimeUpdateRef.current) {
				onTimeUpdateRef.current(video.currentTime, video.duration || 0);
			}
		};

		const handleAudioTimeUpdate = () => {
			if (document.hidden && onTimeUpdateRef.current) {
				onTimeUpdateRef.current(
					audio.currentTime,
					video.duration || audio.duration || 0,
				);
			}
		};

		video.addEventListener("loadedmetadata", handleLoadedMetadata);
		video.addEventListener("play", handlePlay);
		video.addEventListener("pause", handlePause);
		video.addEventListener("volumechange", handleVolumeChange);
		video.addEventListener("seeked", handleSeeked);
		video.addEventListener("timeupdate", handleVideoTimeUpdate);
		audio.addEventListener("timeupdate", handleAudioTimeUpdate);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			video.removeEventListener("loadedmetadata", handleLoadedMetadata);
			video.removeEventListener("play", handlePlay);
			video.removeEventListener("pause", handlePause);
			video.removeEventListener("volumechange", handleVolumeChange);
			video.removeEventListener("seeked", handleSeeked);
			video.removeEventListener("timeupdate", handleVideoTimeUpdate);
			audio.removeEventListener("timeupdate", handleAudioTimeUpdate);
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange,
			);
		};
	}, [videoSrc, audioSrc]);

	return (
		<div className="w-full h-full relative flex items-center justify-center bg-black overflow-hidden">
			<video
				ref={videoRef}
				src={videoSrc}
				controls
				playsInline
				className={
					isPortrait
						? "h-full w-auto max-w-full block" // 竖屏：严格撑满高度，宽度按比例自适应
						: "w-full h-full object-contain block" // 横屏：按容器自适应包含
				}
			/>
			{/*
            音频标签保持 1px 并放置在视口角落，
            避免 display:none 导致浏览器将其直接休眠
          */}
			<audio
				ref={audioRef}
				src={audioSrc}
				preload="auto"
				style={{
					position: "absolute",
					width: "1px",
					height: "1px",
					opacity: 0.01,
					pointerEvents: "none",
				}}
			/>
		</div>
	);
});

export default BVPlayer;
